import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BarChart3, AlertTriangle, SearchCheck, ListChecks, FileCheck,
  GraduationCap, Eye, ClipboardX, ShieldCheck, Recycle, Wrench, ClipboardList,
  ClipboardCheck, FileText, Settings as SettingsIcon, Shield, LogOut
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useHSE } from "../../lib/store";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Executive Dashboard", icon: LayoutDashboard, end: true },
  { to: "/monthly-kpi", label: "Monthly KPI Data", icon: BarChart3 },
  { to: "/incidents", label: "Incidents", icon: AlertTriangle },
  { to: "/inspections", label: "Inspections", icon: SearchCheck },
  { to: "/action-plan", label: "Action Plan", icon: ListChecks },
  { to: "/permit-to-work", label: "Permit to Work", icon: FileCheck },
  { to: "/training", label: "Training", icon: GraduationCap },
  { to: "/observations", label: "Observations / SOR", icon: Eye },
  { to: "/ncr-register", label: "NCR Register", icon: ClipboardX },
  { to: "/audits", label: "Audits", icon: ShieldCheck },
  { to: "/waste-environment", label: "Waste / Environment", icon: Recycle },
  { to: "/equipment-tools", label: "Equipment & Power Tools", icon: Wrench },
  { to: "/checklist-templates", label: "Checklist Templates", icon: ClipboardList },
  { to: "/daily-safety-checklist", label: "Daily Safety Checklist", icon: ClipboardCheck },
  { to: "/reports", label: "Reports / Export", icon: FileText },
  { to: "/settings", label: "Settings / Master Data", icon: SettingsIcon },
];

export default function Sidebar() {
  const { logout, session } = useAuth();
  const { data } = useHSE();
  const nav = useNavigate();
  const clientName = data.settings?.client || "Muhammad Waqas Younas";
  const companyName = data.settings?.company || "MWY Construction HSE & Internal Audit System";
  const onLogout = () => {
    if (!confirm("Sign out of the command center?")) return;
    logout();
    toast.success("Signed out securely");
    nav("/login", { replace: true });
  };
  return (
    <aside
      data-testid="app-sidebar"
      className="hidden lg:flex flex-col w-[240px] shrink-0 h-screen sticky top-0"
      style={{ background: "#082B4D" }}
    >
      <div className="px-5 pt-5 pb-4 border-b border-white/8">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "#18B84F" }}>
            <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="text-white font-heading font-bold text-[15px] tracking-tight">MWY Construction</div>
            <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#18B84F]">HSE & Audit</div>
          </div>
        </div>
        <div className="mt-4 rounded-lg bg-white/6 border border-white/10 px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.18em] text-white/55 font-semibold">Client</div>
          <div className="text-white text-sm font-semibold mt-0.5 truncate">{clientName}</div>
          <div className="text-[10px] text-white/55 truncate">{companyName}</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2.5">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`sidebar-${n.label.toLowerCase().replace(/[^\w]+/g, "-")}-link`}
            className={({ isActive }) =>
              `group relative flex items-center gap-2.5 px-3 py-2 rounded-lg mb-0.5 transition-all duration-150 ${
                isActive
                  ? "text-white shadow-sm"
                  : "text-white/65 hover:bg-white/6 hover:text-white"
              }`
            }
            style={({ isActive }) => isActive ? { background: "#18B84F" } : {}}
          >
            <n.icon className="w-[17px] h-[17px] shrink-0" strokeWidth={1.75} />
            <span className="text-[13px] font-medium">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-2.5 pb-3 pt-2 border-t border-white/8">
        <button
          data-testid="sidebar-logout-btn"
          onClick={onLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-rose-500/15 transition"
        >
          <LogOut className="w-[17px] h-[17px] shrink-0" strokeWidth={1.75} />
          <span className="text-[13px] font-medium">Sign out</span>
          {session?.user?.username && (
            <span className="ml-auto text-[10px] text-white/40 font-mono truncate max-w-[80px]">@{session.user.username}</span>
          )}
        </button>
      </div>

      <div className="px-4 py-3 border-t border-white/8 text-[10px] text-white/40 font-mono">
        v2.0 - {new Date().getFullYear()} MWY HSE
      </div>
    </aside>
  );
}
